import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import Tooltip from "@mui/material/Tooltip";
import List from "@mui/material/List";
import { useTheme } from "@mui/material/styles";
import ReceiverInSendList, { generateReceiverKey } from "./component/ReceiverInSendList";
import ReceiverApproveDialog from "./dialog/ReceiverApproveDialog";
import { useSnackbar, VariantType } from "notistack";
import { arrayRemove } from "./util/Everything";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { ADMIN } from "../ListItems";
import { api } from "../api/api";
import ReceiverAutocomplete from "../components/ReceiverAutocomplete";
import { ModuleHeader } from "../../components/composed";
import { Card } from "../../components/primitives";
import { FadeIn, SlideUp } from "../../components/motion";
import { HermesUser, Receiver } from "../api/models";
import { ModuleAccent } from "../../components/primitives/Card";

// Extended Receiver type with emailSendType property
interface ReceiverWithEmailType extends Receiver {
  emailSendType?: string;
  alternative?: boolean;
  baseName?: string;
}

// Email type definition
interface EmailType {
  value: string;
  name: string;
}

// Props interface for SenderView
interface SenderViewProps {
  user: HermesUser;
  santaState: number;
  onFetchFailed: (error: any) => void;
  setShowSearch: (show: boolean) => void;
  searchValue?: string | null;
}

export default function SenderView(props: SenderViewProps): JSX.Element {
  const theme = useTheme();

  useEffect(() => {
    props.setShowSearch(false);
  }, [props]);

  const [selectedReceivers, setSelectedReceivers] = useState<ReceiverWithEmailType[]>([]);

  const { enqueueSnackbar } = useSnackbar();

  const [emailType, setEmailType] = useState<string>("regular");

  // Module accent for cards
  const moduleAccent: ModuleAccent = 'email';

  const emailTypes = (): EmailType[] => {
    const res: EmailType[] = [
      { value: "regular", name: "Regular" },
      { value: "softlanding", name: "Soft landing" },
      { value: "insurance_card", name: "Cigna card" },
    ];
    if (props.user.roles.includes(ADMIN) && props.santaState === 3) {
      res.push({ value: "santa_present", name: "Secret Santa" });
    }

    return res;
  };

  const handleEmailTypeChange = (e: SelectChangeEvent<string>): void => {
    setEmailType(e.target.value);
  };

  const snackBarAddMessage = (message: string, variant: VariantType): void => {
    enqueueSnackbar(message, { variant });
  };

  function handleReceiverEmailTypeChange(receiver: ReceiverWithEmailType): (value: string) => void {
    return (value: string): void => {
      receiver.emailSendType = value;
    };
  }

  const handleReceiverAdd = (receiver: ReceiverWithEmailType | null): void => {
    if (!receiver) return;

    const receivers = selectedReceivers.slice(0, selectedReceivers.length);
    receiver.emailSendType = emailType;
    receivers.push(receiver);
    setSelectedReceivers(receivers);
  };

  const handleOnItemClear = (receiver: ReceiverWithEmailType): void => {
    let receivers = selectedReceivers.slice(0, selectedReceivers.length);
    receivers = arrayRemove(receivers, receiver);
    setSelectedReceivers(receivers);
  };

  const handleOnSend = (): void => {
    setDialogOpen(true);
  };

  //================ Dialog ========================
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleDialogClose = (): void => {
    setDialogOpen(false);
  };

  const handleDialogApprove = (): void => {
    setDialogOpen(false);
    selectedReceivers.forEach((receiver) => {
      api.sender
        .send(receiver.emailSendType || "regular", receiver.id)
        .then((text: string) => {
          addMessageToSnack(receiver, text);
        })
        .catch((error: any) => {
          props.onFetchFailed(error);
        });
    });
    setSelectedReceivers([]);
  };

  const addMessageToSnack = (receiver: ReceiverWithEmailType, result: string): void => {
    const type: VariantType = result === `"OK"` ? "success" : "info";
    const emailTypeObj = emailTypes().find(
      (res) => res.value === receiver.emailSendType
    );
    const emailTypeName = emailTypeObj ? emailTypeObj.name : "Email";
    const message =
      result === `"OK"`
        ? `Sending ${emailTypeName} email to ${receiver.name}`
        : `Email to ${receiver.name} has already been sent today`;
    snackBarAddMessage(message, type);
  };

  //================ /Dialog ========================

  return (
    <FadeIn>
      <ModuleHeader
        title="Send Emails"
        subtitle="Send notification emails to recipients"
        moduleColor={theme.palette.email?.main || theme.palette.info.main}
      />

      <ReceiverApproveDialog
        component={"span"}
        open={dialogOpen}
        handleClose={handleDialogClose}
        handleApprove={handleDialogApprove}
        handleOnItemClear={handleOnItemClear}
        emailTypes={emailTypes()}
        receivers={selectedReceivers}
      />

      <SlideUp delay={0.1}>
        <Card
          sx={{
            maxWidth: 600,
            mx: "auto",
            mt: 3,
            p: 3,
          }}
          accent={moduleAccent}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={9}>
              <ReceiverAutocomplete
                selectedReceivers={selectedReceivers}
                handleReceiverAdd={handleReceiverAdd}
                onFetchFailed={props.onFetchFailed}
              />
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'center' } }}>
              <Tooltip title="Send">
                <IconButton
                  color="success"
                  aria-label="Send notifications"
                  component="span"
                  disabled={selectedReceivers.length === 0}
                  onClick={handleOnSend}
                  size="large"
                >
                  <SendIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <Select
                variant="standard"
                value={emailType}
                onChange={handleEmailTypeChange}
                fullWidth
              >
                {emailTypes().map((res) => (
                  <MenuItem key={res.value} value={res.value}>
                    {res.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Card>
      </SlideUp>

      {selectedReceivers.length > 0 && (
        <SlideUp delay={0.2}>
          <Card
            sx={{
              maxWidth: 600,
              mx: "auto",
              mt: 3,
              p: 3,
            }}
            accent={moduleAccent}
          >
            <List>
              {selectedReceivers.map((receiver) => (
                <ReceiverInSendList
                  key={generateReceiverKey(receiver)}
                  receiver={receiver}
                  handleOnItemClear={handleOnItemClear}
                  emailTypes={emailTypes()}
                  handleCurrentEmailTypeChange={handleReceiverEmailTypeChange(
                    receiver
                  )}
                  changeCurrentEmailType={true}
                />
              ))}
            </List>
          </Card>
        </SlideUp>
      )}
    </FadeIn>
  );
}
